import { useState, useMemo } from 'react'
import { useDaysToClose } from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ReportFilters, getDateRange as getReportDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Download, Clock, TrendingUp, Target, Calendar, Loader2 } from 'lucide-react'
// formatCurrency is available but unused - keeping import for potential future use
import { formatCurrency as _formatCurrency, formatDate, toDisplayDate } from '@/lib/utils'
import {
  CHART_COLORS,
  DEFAULT_CHART_MARGIN,
  ROTATED_LABEL_CHART_MARGIN,
  CATEGORICAL,
  SEMANTIC,
} from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'

const COLORS = CHART_COLORS

export default function DaysToClose() {
  const [filters, setFilters] = useState<ReportFilterValues>(() =>
    getReportDateRange('last_12_months')
  )

  const { data, isLoading, error } = useDaysToClose({
    startDate: filters.startDate,
    endDate: filters.endDate,
    ...(filters.owner_id ? { owner_id: filters.owner_id } : {}),
  })

  // Transform by-stage data for bar chart
  const byStageChartData = useMemo(() => {
    if (!data?.byStage) return []
    return data.byStage.map((item: any) => ({
      stage: item.stage_name,
      avgDays: parseFloat(item.avg_days || '0'),
      count: item.opportunity_count,
      winRate: parseFloat(item.win_rate || '0'),
    }))
  }, [data])

  // Transform trends data for line chart
  const trendsChartData = useMemo(() => {
    if (!data?.trends) return []
    return data.trends.map((item: any) => ({
      month: (toDisplayDate(item.month) ?? new Date(item.month)).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      }),
      avgDays: parseFloat(item.avg_days || '0'),
      closedCount: item.closed_count,
      wonCount: item.won_count,
    }))
  }, [data])

  // Transform by-deal-size data for bar chart
  const byDealSizeChartData = useMemo(() => {
    if (!data?.byDealSize) return []
    return data.byDealSize.map((item: any) => ({
      size: item.deal_size,
      avgDays: parseFloat(item.avg_days || '0'),
      count: item.opportunity_count,
      avgAmount: parseFloat(item.avg_amount || '0'),
    }))
  }, [data])

  const overall = data?.overall

  const handleExportCSV = () => {
    if (!data?.byStage) return

    const exportData = data.byStage.map((item: any) => ({
      Stage: item.stage_name,
      'Avg Days to Close': item.avg_days,
      'Opportunity Count': item.opportunity_count,
      'Won Count': item.won_count,
      'Win Rate': `${item.win_rate}%`,
    }))

    exportToCSV({ data: exportData, filename: 'days-to-close-analysis.csv' })
  }

  // Check if we have data but it's empty
  const hasEmptyResults =
    !isLoading && !error && data && (!data.byStage || data.byStage.length === 0)

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Days to Close Analysis</h1>
          <p className="text-muted-foreground">Analyze sales cycle length and velocity metrics</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-destructive font-medium">
                Failed to load days to close analysis data.
              </p>
              <p className="text-sm text-muted-foreground">
                Error: {explainError(error, 'Unknown error occurred')}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                aria-label="Reload page"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Days to Close Analysis</h1>
          <p className="text-muted-foreground">Analyze sales cycle length and velocity metrics</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="last_12_months"
            datePresets={[
              'this_month',
              'last_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'last_12_months',
              'all',
              'custom',
            ]}
          />
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={isLoading || !data?.byStage}
            aria-label="Export days to close analysis to CSV"
            className="self-end"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Empty State Warning */}
      {hasEmptyResults && (
        <Card
          style={{ backgroundColor: SEMANTIC.warning.fill, borderColor: SEMANTIC.warning.fill }}
        >
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                No closed opportunities found in the selected date range
              </p>
              <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
                {filters.startDate && filters.endDate ? (
                  <>
                    Your filter from {formatDate(filters.startDate)} to{' '}
                    {formatDate(filters.endDate)} returned no results. Try adjusting your date range
                    or clearing the filters to see all data.
                  </>
                ) : (
                  'No closed opportunity data available. Try clearing the filters or selecting a different date range.'
                )}
              </p>
              {(filters.startDate || filters.endDate) && (
                <Button
                  onClick={() => setFilters(getReportDateRange('last_12_months'))}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  aria-label="Clear date range filters"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label="Loading days to close analysis data"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Avg Days to Close</CardTitle>
                <Clock
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.navy.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {overall?.avg_days_to_close?.toFixed(1) || '0.0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">days average</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Median Days</CardTitle>
                <Target
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {overall?.median_days?.toFixed(1) || '0.0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  50th percentile
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Fastest Close</CardTitle>
                <TrendingUp
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.plum.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {overall?.min_days?.toFixed(1) || '0.0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">days minimum</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Total Closed</CardTitle>
                <Calendar
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.steel.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {overall?.total_closed?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">opportunities</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* By Stage Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Average Days to Close by Stage</CardTitle>
                <CardDescription>Sales cycle length from each final stage</CardDescription>
              </CardHeader>
              <CardContent>
                {byStageChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No stage data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={byStageChartData} margin={ROTATED_LABEL_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="stage"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => {
                          if (name === 'Avg Days') return `${value.toFixed(1)} days`
                          if (name === 'Win Rate') return `${value.toFixed(1)}%`
                          return value
                        }}
                      />
                      <Legend />
                      <Bar dataKey="avgDays" fill={COLORS[0]} name="Avg Days" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* By Deal Size Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Days to Close by Deal Size</CardTitle>
                <CardDescription>How deal size affects sales cycle length</CardDescription>
              </CardHeader>
              <CardContent>
                {byDealSizeChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No deal size data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={byDealSizeChartData} margin={DEFAULT_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="size" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => {
                          if (name === 'Avg Days') return `${value.toFixed(1)} days`
                          return value
                        }}
                      />
                      <Legend />
                      <Bar dataKey="avgDays" fill={COLORS[1]} name="Avg Days" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Trends Line Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Days to Close Trends</CardTitle>
              <CardDescription>Monthly average sales cycle length over time</CardDescription>
            </CardHeader>
            <CardContent>
              {trendsChartData.length === 0 ? (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  No trends data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendsChartData} margin={DEFAULT_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number, name: string) => {
                        if (name === 'Avg Days') return `${value.toFixed(1)} days`
                        return value
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="avgDays"
                      stroke={COLORS[2]}
                      strokeWidth={2}
                      name="Avg Days"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Metrics by Stage</CardTitle>
              <CardDescription>Complete breakdown of sales cycle metrics</CardDescription>
            </CardHeader>
            <CardContent>
              {!data?.byStage || data.byStage.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  No detailed metrics available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm"
                    role="table"
                    aria-label="Days to close detailed metrics"
                  >
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Stage</th>
                        <th className="text-right p-2 font-medium">Avg Days</th>
                        <th className="text-right p-2 font-medium">Total Opps</th>
                        <th className="text-right p-2 font-medium">Won Count</th>
                        <th className="text-right p-2 font-medium">Win Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byStage.map((item: any, index: number) => (
                        <tr key={index} className="border-b hover:bg-muted/50">
                          <td className="p-2">{item.stage_name}</td>
                          <td className="text-right p-2">
                            {parseFloat(item.avg_days || '0').toFixed(1)}
                          </td>
                          <td className="text-right p-2">{item.opportunity_count}</td>
                          <td className="text-right p-2">{item.won_count}</td>
                          <td className="text-right p-2">
                            {parseFloat(item.win_rate || '0').toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
